import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined'
import { useAppStore } from '@/store/appStore'
import { THEME_PREFERENCES, type ThemePreference } from '@/theme/themeMode'

const PREFERENCE_ICONS: Record<ThemePreference, typeof LightModeOutlinedIcon> = {
  light: LightModeOutlinedIcon,
  dark: DarkModeOutlinedIcon,
  system: SettingsBrightnessOutlinedIcon,
}

const PREFERENCE_LABEL_KEYS: Record<ThemePreference, string> = {
  light: 'header.themeLight',
  dark: 'header.themeDark',
  system: 'header.themeSystem',
}

/** Header picker for light / dark / follow-the-system. Only rendered while DARK_MODE_ENABLED. */
export function ThemeModeMenu() {
  const { t } = useTranslation()
  const themePreference = useAppStore((s) => s.themePreference)
  const setThemePreference = useAppStore((s) => s.setThemePreference)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const CurrentIcon = PREFERENCE_ICONS[themePreference]

  const choose = (preference: ThemePreference) => {
    setThemePreference(preference)
    setAnchorEl(null)
  }

  return (
    <>
      <Tooltip title={t('header.colorTheme')} placement="bottom">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t('header.colorTheme')}
          aria-haspopup="menu"
          aria-expanded={anchorEl !== null}
          sx={{ color: 'var(--color-white)', '&:hover': { backgroundColor: 'var(--color-on-dark-15)' } }}
        >
          <CurrentIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {THEME_PREFERENCES.map((preference) => {
          const Icon = PREFERENCE_ICONS[preference]
          const selected = preference === themePreference
          return (
            <MenuItem
              key={preference}
              selected={selected}
              onClick={() => choose(preference)}
              role="menuitemradio"
              aria-checked={selected}
            >
              <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
              <ListItemText>{t(PREFERENCE_LABEL_KEYS[preference])}</ListItemText>
              <CheckIcon fontSize="small" sx={{ ml: 2, visibility: selected ? 'visible' : 'hidden' }} />
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
